// Reporting, Section 8: program totals, per customer and per franchisee
// views, month over month, and the two downloads. Figures come from the
// monthly imports; a month's report covers whatever has been imported for it
// so far.

import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { getMonthlyReport, reportCsvUrl, reportPdfUrl } from '../api.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import {
  Button,
  ButtonLink,
  EmptyState,
  Kpi,
  KpiRow,
  Notice,
  Panel,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import {
  formatMoney,
  formatCount,
  monthLabel,
  currentMonth,
  shiftMonth,
  ACTIVITY_SOURCES,
  ACTIVITY_SOURCE,
} from '../vocab.js'

function TotalsTable({ title, rows, nameLabel }) {
  return (
    <Panel title={title} flush>
      {rows.length === 0 ? (
        <EmptyState title="Nothing recorded">
          Figures appear once the month's documents are imported.
        </EmptyState>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>{nameLabel}</Th>
              <Th numeric>Lines</Th>
              <Th numeric>Tests</Th>
              <Th numeric>Spend</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.name ?? row.serviceMonth}>
                <Td>{row.serviceMonth ? monthLabel(row.serviceMonth) : row.name}</Td>
                <Td numeric>{formatCount(row.lines)}</Td>
                <Td numeric>{formatCount(row.tests) ?? '0'}</Td>
                <Td numeric>{formatMoney(row.spend)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Panel>
  )
}

export default function Reports() {
  const { can } = useAuth()
  const canReadAudit = can(ACTION.AUDIT_READ)
  const canReadBills = can(ACTION.CUSTOMER_BILLS_READ)
  const [month, setMonth] = useState(currentMonth())
  const [source, setSource] = useState(ACTIVITY_SOURCE.FRANCHISEE_INVOICE)
  const [report, setReport] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const load = useCallback(async () => {
    try {
      const data = await getMonthlyReport(month, source)
      setReport(data.report)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [month, source])

  useEffect(() => {
    load()
  }, [load])

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }

  const totals = report?.totals

  return (
    <>
      <div className="toolbar">
        <Button
          variant="icon"
          aria-label="Previous month"
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          <ChevronLeft size={16} strokeWidth={1.75} />
        </Button>
        <span className="month-label">{monthLabel(month)}</span>
        <Button
          variant="icon"
          aria-label="Next month"
          onClick={() => setMonth(shiftMonth(month, 1))}
        >
          <ChevronRight size={16} strokeWidth={1.75} />
        </Button>
        {canReadBills ? (
          <Select
            value={source}
            onChange={setSource}
            ariaLabel="Which source to report"
            style={{ width: '14rem' }}
            options={ACTIVITY_SOURCES.map((item) => ({ value: item.value, label: item.label }))}
          />
        ) : null}
        <span className="spacer" />
        {canReadAudit ? <ButtonLink to="/reports/audit">Audit log</ButtonLink> : null}
        <a className="ui-btn ui-btn-secondary" href={reportCsvUrl(month, source)}>
          Download CSV
        </a>
        <a className="ui-btn ui-btn-primary" href={reportPdfUrl(month, source)}>
          Download PDF
        </a>
      </div>

      <Notice tone="neutral">
        {report?.sourceLabel ?? 'Figures'} for the month, covering what has been imported. The CSV
        leaves for manual reconciliation in QuickBooks Online, which remains the system of record.
      </Notice>

      {totals ? (
        <KpiRow>
          <Kpi lead label="Total spend" value={formatMoney(totals.spend)} />
          <Kpi label="Tests performed" value={formatCount(totals.tests) ?? '0'} />
          <Kpi label="Activity lines" value={formatCount(totals.lines) ?? '0'} />
          <Kpi label="Customers with activity" value={String(totals.customers)} />
        </KpiRow>
      ) : null}

      {report ? (
        <div className="split">
          <TotalsTable title="By customer" rows={report.byCustomer} nameLabel="Customer" />
          <TotalsTable title="By franchisee" rows={report.byFranchisee} nameLabel="Franchisee" />
        </div>
      ) : null}

      {report ? (
        <TotalsTable title="Month over month" rows={report.trailing} nameLabel="Month" />
      ) : null}
    </>
  )
}
